import * as SelectPrimitive from "@radix-ui/react-select";
import { ComponentProps, ReactNode, useMemo, useState } from "react";
import t from "@/lang/lang";
import { Check, ChevronDown } from "lucide-react";
import { useCustomersCustomerListQuery } from "@/services/backend/customers/api/customer-api";
import { FilterMode } from "@/shared/lib/filter/filter";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { CustomerAddressLine } from "./customer-address-line";
import { Popover, PopoverContent } from "@/shared/components/ui/popover";
import { PopoverTrigger } from "@radix-ui/react-popover";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/shared/components/ui/command";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Spinner } from "@/shared/components/ui/spinner";
import { cn } from "@/shared/lib/utils";
import { CustomerName } from "./customer-name";
import { oneLine } from "../../addresses/types/address";

export function CustomerSelect({
  value,
  onValueChange,
  showUnsetOption = false,
  note,
}: ComponentProps<typeof SelectPrimitive.Root> & {
  showUnsetOption?: boolean;
  note?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);
  const { data, isLoading, isFetching } = useCustomersCustomerListQuery(
    {
      name: {
        active: true,
        mode: FilterMode.Like,
        values: [debouncedSearch],
      },
      limit: 100,
    },
    { skip: !debouncedSearch || debouncedSearch.length < 3 },
  );

  const customers = useMemo(() => {
    if (search.length === 0) {
      return [];
    }
    return data?.items ?? [];
  }, [data, search.length]);

  const doSelect = (customerId: UUID) => {
    onValueChange?.(customerId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild className="@container">
        <Button variant="outline" className="w-full">
          {!value || value === NullUUID ? (
            <span className="text-muted-foreground font-normal">
              {t("Kunde auswählen ...")}
            </span>
          ) : (
            <span>
              <CustomerName customerId={value} />
              <CustomerAddressLine
                customerId={value}
                className="text-muted-foreground ml-2 text-xs font-normal @max-sm:hidden"
              />
            </span>
          )}
          <ChevronDown className="ml-auto opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[400px] p-0">
        {note && (
          <div className="text-muted-foreground border-b p-2 text-xs">
            {note}
          </div>
        )}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t("Suche einen Kunden ...")}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {(isLoading || isFetching) && (
              <div className="flex items-center justify-center py-4">
                <Spinner />
              </div>
            )}
            {customers.length === 0 && (
              <div className="pt-2 text-center text-sm">
                {t("Keine Suchergebnisse")}
              </div>
            )}
            <CommandGroup>
              {customers.map((customer) => (
                <CommandItem
                  key={customer.id}
                  value={customer.id}
                  onSelect={() => doSelect(customer.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === customer.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col">
                    {customer.name}
                    <span className="text-muted-foreground text-xs">
                      {oneLine(customer.address)}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {showUnsetOption && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value={NullUUID}
                    onSelect={() => doSelect(NullUUID)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === NullUUID ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {t("Kein Kunde auswählen")}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
